import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import {
  GATECHECK_ALLOWED_DECISIONS,
  buildGatecheckRecord,
  buildTemplateMetaFromRecord,
  createEmptyContractorProfile,
  createEmptyDecisionDraft,
  createEmptyTenderProfile,
  createWorkspaceFromDemo,
  duplicateAction,
  duplicateFinding,
  duplicateHistoryEntry,
  getGatecheckPresetOptions,
  validateGatecheckWorkspace,
} from "../utils/gatecheckBuilder";
import "./GatecheckTemplatePage.css";

const GET_MENU_API_URL = awsConfig.api_url_get_menu;
const defaultPreset = getGatecheckPresetOptions()[0] || null;

function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

function sanitizeSlug(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 50);
}

function buildStorageKey(businessId, suffix) {
  return `gatecheck:${businessId || "default"}:${suffix}`;
}

function parseSavedJson(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function SavedProfileSelector({
  savedProfiles,
  selectedProfileId,
  onSelect,
  onDelete,
}) {
  if (!savedProfiles.length) return null;
  return (
    <div className="gatecheck-template-page__saved-tools">
      <label>
        Saved contractor profile
        <select
          value={selectedProfileId}
          onChange={(event) => onSelect(event.target.value)}
        >
          <option value="">Choose a saved profile</option>
          {savedProfiles.map((profile) => (
            <option key={profile.profileId} value={profile.profileId}>
              {profile.contractorName || "Untitled profile"}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="gatecheck-template-page__ghost-button"
        onClick={() => onDelete(selectedProfileId)}
        disabled={!selectedProfileId}
      >
        Delete saved profile
      </button>
    </div>
  );
}

function ChecklistCard({ title, items }) {
  return (
    <section className="gatecheck-template-page__card">
      <h2>{title}</h2>
      <ul className="gatecheck-template-page__checklist">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

export default function GatecheckTemplatePage({ business }) {
  const initialWorkspace = useMemo(() => {
    if (!defaultPreset) {
      return {
        contractorProfile: createEmptyContractorProfile(),
        tenderProfile: createEmptyTenderProfile(),
        decisionDraft: createEmptyDecisionDraft(),
        storeSlug: "gatecheck-demo",
      };
    }
    return createWorkspaceFromDemo(
      defaultPreset.contractorSlug,
      defaultPreset.projectSlug
    );
  }, []);

  const [selectedPresetKey, setSelectedPresetKey] = useState(
    defaultPreset ? defaultPreset.key : ""
  );
  const [contractorProfile, setContractorProfile] = useState(
    initialWorkspace.contractorProfile
  );
  const [tenderProfile, setTenderProfile] = useState(initialWorkspace.tenderProfile);
  const [decisionDraft, setDecisionDraft] = useState(initialWorkspace.decisionDraft);
  const [storeSlug, setStoreSlug] = useState(initialWorkspace.storeSlug);
  const [publishes, setPublishes] = useState([]);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const [publishResult, setPublishResult] = useState(null);

  const presets = useMemo(() => getGatecheckPresetOptions(), []);
  const storageKeys = useMemo(
    () => ({
      profiles: buildStorageKey(business?.businessId, "profiles"),
      draft: buildStorageKey(business?.businessId, "draft"),
    }),
    [business?.businessId]
  );

  const getBearer = useCallback(async () => {
    const { tokens } = await fetchAuthSession();
    const bearer = resolveAuthToken(tokens);
    if (!bearer) throw new Error("Session expired. Please sign in again.");
    return bearer;
  }, []);

  const loadPublishes = useCallback(async () => {
    if (!business?.businessId) return;
    setLoading(true);
    setError("");
    try {
      const bearer = await getBearer();
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body?.error || "Failed to load your published links.");
      }
      const active = body?.publishes?.active || [];
      setPublishes(
        active.filter((entry) => (entry.template || "").toLowerCase() === "gatecheck")
      );
    } catch (err) {
      setError(err.message || "Failed to load your published links.");
    } finally {
      setLoading(false);
    }
  }, [business?.businessId, getBearer]);

  useEffect(() => {
    loadPublishes();
  }, [loadPublishes]);

  useEffect(() => {
    const draft = parseSavedJson(
      window.localStorage.getItem(storageKeys.draft),
      null
    );
    if (draft) {
      setSelectedPresetKey(draft.selectedPresetKey || "");
      setContractorProfile(draft.contractorProfile || createEmptyContractorProfile());
      setTenderProfile(draft.tenderProfile || createEmptyTenderProfile());
      setDecisionDraft(draft.decisionDraft || createEmptyDecisionDraft());
      setStoreSlug(draft.storeSlug || "gatecheck-demo");
    }
    setSavedProfiles(
      parseSavedJson(window.localStorage.getItem(storageKeys.profiles), [])
    );
  }, [storageKeys.draft, storageKeys.profiles]);

  useEffect(() => {
    const draftPayload = {
      selectedPresetKey,
      contractorProfile,
      tenderProfile,
      decisionDraft,
      storeSlug,
    };
    window.localStorage.setItem(storageKeys.draft, JSON.stringify(draftPayload));
  }, [
    contractorProfile,
    decisionDraft,
    selectedPresetKey,
    storageKeys.draft,
    storeSlug,
    tenderProfile,
  ]);

  const gatecheckRecord = useMemo(
    () => buildGatecheckRecord(contractorProfile, tenderProfile, decisionDraft),
    [contractorProfile, tenderProfile, decisionDraft]
  );

  const validationErrors = useMemo(
    () =>
      validateGatecheckWorkspace(
        contractorProfile,
        tenderProfile,
        decisionDraft
      ),
    [contractorProfile, decisionDraft, tenderProfile]
  );

  const handlePresetChange = (event) => {
    const value = event.target.value;
    setSelectedPresetKey(value);
    if (!value) return;
    const [contractorSlug, projectSlug] = value.split("__");
    const workspace = createWorkspaceFromDemo(contractorSlug, projectSlug);
    setContractorProfile(workspace.contractorProfile);
    setTenderProfile(workspace.tenderProfile);
    setDecisionDraft(workspace.decisionDraft);
    setStoreSlug(workspace.storeSlug);
    setSelectedProfileId("");
    setProfileMessage("");
  };

  const updateContractor = (field, value) => {
    setContractorProfile((current) => ({ ...current, [field]: value }));
  };

  const updateTender = (field, value) => {
    setTenderProfile((current) => ({ ...current, [field]: value }));
  };

  const updateDecision = (field, value) => {
    setDecisionDraft((current) => ({ ...current, [field]: value }));
  };

  const updateFinding = (index, field, value) => {
    setDecisionDraft((current) => ({
      ...current,
      findings: current.findings.map((finding, findingIndex) =>
        findingIndex === index ? { ...finding, [field]: value } : finding
      ),
    }));
  };

  const updateAction = (index, field, value) => {
    setDecisionDraft((current) => ({
      ...current,
      nextActions: current.nextActions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, [field]: value } : action
      ),
    }));
  };

  const updateHistoryEntry = (index, field, value) => {
    setDecisionDraft((current) => ({
      ...current,
      decisionHistory: current.decisionHistory.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const addFinding = () => {
    setDecisionDraft((current) => ({
      ...current,
      findings: [...current.findings, duplicateFinding()],
    }));
  };

  const removeFinding = (index) => {
    setDecisionDraft((current) => ({
      ...current,
      findings:
        current.findings.length === 1
          ? [duplicateFinding()]
          : current.findings.filter((_, findingIndex) => findingIndex !== index),
    }));
  };

  const addAction = () => {
    setDecisionDraft((current) => ({
      ...current,
      nextActions: [...current.nextActions, duplicateAction()],
    }));
  };

  const removeAction = (index) => {
    setDecisionDraft((current) => ({
      ...current,
      nextActions:
        current.nextActions.length === 1
          ? [duplicateAction()]
          : current.nextActions.filter((_, actionIndex) => actionIndex !== index),
    }));
  };

  const addHistoryEntry = () => {
    setDecisionDraft((current) => ({
      ...current,
      decisionHistory: [...current.decisionHistory, duplicateHistoryEntry()],
    }));
  };

  const removeHistoryEntry = (index) => {
    setDecisionDraft((current) => ({
      ...current,
      decisionHistory:
        current.decisionHistory.length === 1
          ? [duplicateHistoryEntry()]
          : current.decisionHistory.filter((_, entryIndex) => entryIndex !== index),
    }));
  };

  const saveContractorProfile = () => {
    if (!contractorProfile.contractorName.trim()) {
      setProfileMessage("Add a contractor name before saving the reusable profile.");
      return;
    }
    setSavingProfile(true);
    const profileId =
      contractorProfile.profileId ||
      `profile-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = {
      ...contractorProfile,
      profileId,
      savedAt: new Date().toISOString(),
    };
    const nextProfiles = [...savedProfiles];
    const existingIndex = nextProfiles.findIndex((profile) => profile.profileId === profileId);
    if (existingIndex >= 0) {
      nextProfiles[existingIndex] = payload;
    } else {
      nextProfiles.unshift(payload);
    }
    setSavedProfiles(nextProfiles);
    setSelectedProfileId(profileId);
    setContractorProfile((current) => ({ ...current, profileId }));
    window.localStorage.setItem(storageKeys.profiles, JSON.stringify(nextProfiles));
    setProfileMessage("Contractor profile saved for reuse.");
    setSavingProfile(false);
  };

  const loadSavedProfile = (profileId) => {
    setSelectedProfileId(profileId);
    if (!profileId) return;
    const profile = savedProfiles.find((entry) => entry.profileId === profileId);
    if (!profile) return;
    setContractorProfile(profile);
    setProfileMessage("Saved contractor profile loaded into the builder.");
  };

  const deleteSavedProfile = (profileId) => {
    if (!profileId) return;
    const nextProfiles = savedProfiles.filter((profile) => profile.profileId !== profileId);
    setSavedProfiles(nextProfiles);
    setSelectedProfileId("");
    window.localStorage.setItem(storageKeys.profiles, JSON.stringify(nextProfiles));
    if (contractorProfile.profileId === profileId) {
      setContractorProfile((current) => ({ ...current, profileId: "" }));
    }
    setProfileMessage("Saved contractor profile deleted.");
  };

  const resetWorkspace = () => {
    setContractorProfile(createEmptyContractorProfile());
    setTenderProfile(createEmptyTenderProfile());
    setDecisionDraft(createEmptyDecisionDraft());
    setStoreSlug("gatecheck-demo");
    setSelectedPresetKey("");
    setSelectedProfileId("");
    setPublishResult(null);
    setProfileMessage("Builder reset to a blank workspace.");
  };

  const publishFlow = async (event) => {
    event.preventDefault();
    setPublishing(true);
    setError("");
    setPublishResult(null);

    try {
      const trimmedSlug = sanitizeSlug(storeSlug);
      if (!trimmedSlug) {
        throw new Error("Public slug is required.");
      }
      if (validationErrors.length) {
        throw new Error(validationErrors[0]);
      }
      const bearer = await getBearer();
      const record = {
        ...gatecheckRecord,
        lastUpdated: new Date().toISOString(),
      };
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${bearer}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template: "gatecheck",
          storeSlug: trimmedSlug,
          items: [],
          components: [],
          templateMeta: buildTemplateMetaFromRecord(record),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "Could not publish the Gatecheck link.");
      setStoreSlug(trimmedSlug);
      setPublishResult(body);
      setDecisionDraft((current) => ({
        ...current,
        lastUpdated: record.lastUpdated,
      }));
      await loadPublishes();
    } catch (err) {
      setError(err.message || "Could not publish the Gatecheck link.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="gatecheck-template-page">
      <div className="gatecheck-template-page__header">
        <div>
          <p className="gatecheck-template-page__eyebrow">Layerbound Template</p>
          <h1>Gatecheck workspace</h1>
          <p className="gatecheck-template-page__copy">
            Build a reusable contractor profile, compare it against a tender, and publish the live pre-estimate decision link through the existing `get_menu` flow.
          </p>
        </div>
        <div className="gatecheck-template-page__header-actions">
          <button
            type="button"
            className="gatecheck-template-page__ghost-button"
            onClick={resetWorkspace}
          >
            New blank workspace
          </button>
          <Link className="gatecheck-template-page__back" to="/">
            Back to dashboard
          </Link>
        </div>
      </div>

      <div className="gatecheck-template-page__grid">
        <div className="gatecheck-template-page__main">
          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>1. Start point</h2>
                <p className="gatecheck-template-page__muted">
                  Load a demo workspace or start from a blank manual gate.
                </p>
              </div>
            </div>
            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Starter preset
                <select value={selectedPresetKey} onChange={handlePresetChange}>
                  <option value="">Blank workspace</option>
                  {presets.map((preset) => (
                    <option key={preset.key} value={preset.key}>
                      {preset.contractorName} — {preset.projectName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Public slug
                <input
                  value={storeSlug}
                  onChange={(event) => setStoreSlug(sanitizeSlug(event.target.value))}
                  placeholder="gate-queensway-house"
                />
              </label>
            </div>
          </section>

          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>2. Contractor profile</h2>
                <p className="gatecheck-template-page__muted">
                  This is the reusable evidence base. Save it once, then reuse it across tenders.
                </p>
              </div>
              <button
                type="button"
                className="gatecheck-template-page__ghost-button"
                onClick={saveContractorProfile}
                disabled={savingProfile}
              >
                {savingProfile ? "Saving..." : "Save contractor profile"}
              </button>
            </div>

            <SavedProfileSelector
              savedProfiles={savedProfiles}
              selectedProfileId={selectedProfileId}
              onSelect={loadSavedProfile}
              onDelete={deleteSavedProfile}
            />

            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Contractor name
                <input
                  value={contractorProfile.contractorName}
                  onChange={(event) =>
                    updateContractor("contractorName", event.target.value)
                  }
                  placeholder="BS Contractors Ltd"
                />
              </label>
              <label>
                Short name
                <input
                  value={contractorProfile.contractorShortName}
                  onChange={(event) =>
                    updateContractor("contractorShortName", event.target.value)
                  }
                  placeholder="BS Contractors"
                />
              </label>
              <label>
                Sector
                <input
                  value={contractorProfile.sector}
                  onChange={(event) => updateContractor("sector", event.target.value)}
                  placeholder="Passive fire contractor"
                />
              </label>
              <label>
                Legal name
                <input
                  value={contractorProfile.legalName}
                  onChange={(event) =>
                    updateContractor("legalName", event.target.value)
                  }
                  placeholder="Legal entity"
                />
              </label>
              <label>
                Website
                <input
                  value={contractorProfile.website}
                  onChange={(event) => updateContractor("website", event.target.value)}
                  placeholder="https://example.com"
                />
              </label>
              <label>
                Location
                <input
                  value={contractorProfile.location}
                  onChange={(event) => updateContractor("location", event.target.value)}
                  placeholder="Chelmsford, Essex"
                />
              </label>
            </div>

            <div className="gatecheck-template-page__form-grid">
              <label>
                Regions served
                <textarea
                  rows={3}
                  value={contractorProfile.regionsServed}
                  onChange={(event) =>
                    updateContractor("regionsServed", event.target.value)
                  }
                  placeholder={"Essex\nLondon\nSouth East"}
                />
              </label>
              <label>
                Scopes self-delivered
                <textarea
                  rows={4}
                  value={contractorProfile.scopesDelivered}
                  onChange={(event) =>
                    updateContractor("scopesDelivered", event.target.value)
                  }
                  placeholder={"Penetration sealing\nFire boarding\nCompartmentation"}
                />
              </label>
              <label>
                Certifications and exact scope
                <textarea
                  rows={4}
                  value={contractorProfile.certifications}
                  onChange={(event) =>
                    updateContractor("certifications", event.target.value)
                  }
                  placeholder={"FIRAS - scope publicly verified\nISO 9001 - current"}
                />
              </label>
              <label>
                Insurance
                <textarea
                  rows={4}
                  value={contractorProfile.insurance}
                  onChange={(event) =>
                    updateContractor("insurance", event.target.value)
                  }
                  placeholder={"PL: GBP 10m\nEL: GBP 10m\nPI: Needs confirmation"}
                />
              </label>
              <label>
                Relevant reference projects
                <textarea
                  rows={4}
                  value={contractorProfile.referenceProjects}
                  onChange={(event) =>
                    updateContractor("referenceProjects", event.target.value)
                  }
                  placeholder={"Social housing remediation - Essex - 2025\nCommercial office remedials - London - 2024"}
                />
              </label>
              <label>
                Workforce, supervision, subcontracting
                <textarea
                  rows={4}
                  value={contractorProfile.workforceCapacity}
                  onChange={(event) =>
                    updateContractor("workforceCapacity", event.target.value)
                  }
                  placeholder="Named supervisors, labour model, capacity notes."
                />
              </label>
              <label>
                QA evidence and reporting
                <textarea
                  rows={4}
                  value={contractorProfile.qaEvidence}
                  onChange={(event) =>
                    updateContractor("qaEvidence", event.target.value)
                  }
                  placeholder="Photo QA, handover packs, digital reporting, manufacturer sign-off."
                />
              </label>
              <label>
                Strengths
                <textarea
                  rows={4}
                  value={contractorProfile.strengths}
                  onChange={(event) => updateContractor("strengths", event.target.value)}
                  placeholder={"Drylining and making-good interface\nRegional delivery footprint"}
                />
              </label>
              <label>
                Unknowns needing contractor confirmation
                <textarea
                  rows={4}
                  value={contractorProfile.unknowns}
                  onChange={(event) => updateContractor("unknowns", event.target.value)}
                  placeholder={"Exact certification scope\nPI limit and expiry"}
                />
              </label>
            </div>

            {profileMessage ? (
              <p className="gatecheck-template-page__message">{profileMessage}</p>
            ) : null}
          </section>

          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>3. Tender profile</h2>
                <p className="gatecheck-template-page__muted">
                  Enter the opportunity-specific requirements Layerbound should test.
                </p>
              </div>
            </div>

            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Project name
                <input
                  value={tenderProfile.projectName}
                  onChange={(event) => updateTender("projectName", event.target.value)}
                  placeholder="Queensway House"
                />
              </label>
              <label>
                Project subtitle
                <input
                  value={tenderProfile.projectSubtitle}
                  onChange={(event) =>
                    updateTender("projectSubtitle", event.target.value)
                  }
                  placeholder="Passive Fire Tender"
                />
              </label>
              <label>
                Client
                <input
                  value={tenderProfile.client}
                  onChange={(event) => updateTender("client", event.target.value)}
                  placeholder="Northborough Housing Authority"
                />
              </label>
              <label>
                Geography
                <input
                  value={tenderProfile.geography}
                  onChange={(event) => updateTender("geography", event.target.value)}
                  placeholder="Greater London"
                />
              </label>
              <label>
                Estimated value
                <input
                  value={tenderProfile.estimatedValue}
                  onChange={(event) =>
                    updateTender("estimatedValue", event.target.value)
                  }
                  placeholder="GBP 75k - 200k"
                />
              </label>
              <label>
                Deadline
                <input
                  type="date"
                  value={tenderProfile.deadline}
                  onChange={(event) => updateTender("deadline", event.target.value)}
                />
              </label>
              <label>
                Sector
                <input
                  value={tenderProfile.sector}
                  onChange={(event) => updateTender("sector", event.target.value)}
                  placeholder="Social housing"
                />
              </label>
              <label>
                Delivery model
                <input
                  value={tenderProfile.deliveryModel}
                  onChange={(event) =>
                    updateTender("deliveryModel", event.target.value)
                  }
                  placeholder="Main-contractor-led subcontract"
                />
              </label>
            </div>

            <div className="gatecheck-template-page__toggles">
              <label className="gatecheck-template-page__toggle">
                <input
                  type="checkbox"
                  checked={tenderProfile.occupiedBuilding}
                  onChange={(event) =>
                    updateTender("occupiedBuilding", event.target.checked)
                  }
                />
                Occupied building
              </label>
              <label className="gatecheck-template-page__toggle">
                <input
                  type="checkbox"
                  checked={tenderProfile.mainContractorLed}
                  onChange={(event) =>
                    updateTender("mainContractorLed", event.target.checked)
                  }
                />
                Main-contractor-led
              </label>
            </div>

            <div className="gatecheck-template-page__form-grid">
              <label>
                Package scope
                <textarea
                  rows={4}
                  value={tenderProfile.packageScope}
                  onChange={(event) =>
                    updateTender("packageScope", event.target.value)
                  }
                  placeholder="Penetration sealing, fire boarding, compartmentation defects."
                />
              </label>
              <label>
                Tender requirements
                <textarea
                  rows={5}
                  value={tenderProfile.requirements}
                  onChange={(event) => updateTender("requirements", event.target.value)}
                  placeholder={"FIRAS or equivalent\nInsurance limits\nTwo comparable references\nQA photos"}
                />
              </label>
              <label>
                Exclusions or clarifications
                <textarea
                  rows={4}
                  value={tenderProfile.exclusions}
                  onChange={(event) => updateTender("exclusions", event.target.value)}
                  placeholder="Anything not yet confirmed or intentionally excluded."
                />
              </label>
              <label>
                Opportunity summary
                <textarea
                  rows={4}
                  value={tenderProfile.notes}
                  onChange={(event) => updateTender("notes", event.target.value)}
                  placeholder="Why this opportunity matters and what makes it commercially interesting."
                />
              </label>
            </div>
          </section>

          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>4. Gate decision</h2>
                <p className="gatecheck-template-page__muted">
                  Capture the actual decision, the reasons, and the evidence-backed follow-up.
                </p>
              </div>
            </div>

            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Decision status
                <select
                  value={decisionDraft.status}
                  onChange={(event) => updateDecision("status", event.target.value)}
                >
                  {GATECHECK_ALLOWED_DECISIONS.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Badge
                <input
                  value={decisionDraft.badge}
                  onChange={(event) => updateDecision("badge", event.target.value)}
                  placeholder="Manual Gatecheck"
                />
              </label>
              <label>
                Confidence
                <input
                  value={decisionDraft.confidence || ""}
                  onChange={(event) => updateDecision("confidence", event.target.value)}
                  placeholder="Medium"
                />
              </label>
              <label>
                Decision owner
                <input
                  value={decisionDraft.decisionOwner || ""}
                  onChange={(event) => updateDecision("decisionOwner", event.target.value)}
                  placeholder="Commercial Director"
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Recommendation
                <input
                  value={decisionDraft.recommendation}
                  onChange={(event) =>
                    updateDecision("recommendation", event.target.value)
                  }
                  placeholder="Proceed to estimating with conditions tracked."
                />
              </label>
              <label>
                Human decision
                <input
                  value={decisionDraft.humanDecision || ""}
                  onChange={(event) => updateDecision("humanDecision", event.target.value)}
                  placeholder="Awaiting review"
                />
              </label>
              <label>
                Approval status
                <input
                  value={decisionDraft.approvalStatus || ""}
                  onChange={(event) => updateDecision("approvalStatus", event.target.value)}
                  placeholder="Awaiting review"
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Rationale
                <textarea
                  rows={3}
                  value={decisionDraft.rationale}
                  onChange={(event) => updateDecision("rationale", event.target.value)}
                  placeholder="Why this decision is commercially and operationally correct."
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Share-view opportunity summary
                <textarea
                  rows={3}
                  value={decisionDraft.opportunitySummary}
                  onChange={(event) =>
                    updateDecision("opportunitySummary", event.target.value)
                  }
                  placeholder="This appears to be a relevant subcontract, but evidence gaps still need resolving."
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Override reason
                <textarea
                  rows={2}
                  value={decisionDraft.overrideReason || ""}
                  onChange={(event) => updateDecision("overrideReason", event.target.value)}
                  placeholder="Optional. Record why the human decision differs from the recommendation."
                />
              </label>
            </div>

            <div className="gatecheck-template-page__stack">
              <div className="gatecheck-template-page__subsection-head">
                <h3>Findings</h3>
                <button
                  type="button"
                  className="gatecheck-template-page__ghost-button"
                  onClick={addFinding}
                >
                  Add finding
                </button>
              </div>
              {decisionDraft.findings.map((finding, index) => (
                <div key={`finding-${index}`} className="gatecheck-template-page__nested-card">
                  <div className="gatecheck-template-page__subsection-head">
                    <strong>Finding {index + 1}</strong>
                    <button
                      type="button"
                      className="gatecheck-template-page__ghost-button gatecheck-template-page__ghost-button--danger"
                      onClick={() => removeFinding(index)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--three">
                    <label>
                      Type
                      <select
                        value={finding.type}
                        onChange={(event) =>
                          updateFinding(index, "type", event.target.value)
                        }
                      >
                        {[
                          "BLOCKER",
                          "EVIDENCE GAP",
                          "SCOPE DECISION",
                          "CLARIFICATION",
                          "CLEAR",
                        ].map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Severity
                      <select
                        value={finding.severity}
                        onChange={(event) =>
                          updateFinding(index, "severity", event.target.value)
                        }
                      >
                        {["Low", "Medium", "High"].map((severity) => (
                          <option key={severity} value={severity}>
                            {severity}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Owner
                      <input
                        value={finding.owner}
                        onChange={(event) =>
                          updateFinding(index, "owner", event.target.value)
                        }
                        placeholder="Commercial"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Title
                      <input
                        value={finding.title}
                        onChange={(event) =>
                          updateFinding(index, "title", event.target.value)
                        }
                        placeholder="Insurance limit below tender threshold"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Explanation
                      <textarea
                        rows={3}
                        value={finding.explanation}
                        onChange={(event) =>
                          updateFinding(index, "explanation", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Tender requirement
                      <textarea
                        rows={2}
                        value={finding.tenderRequirement}
                        onChange={(event) =>
                          updateFinding(index, "tenderRequirement", event.target.value)
                        }
                      />
                    </label>
                    <label>
                      Tender source
                      <input
                        value={finding.tenderSource}
                        onChange={(event) =>
                          updateFinding(index, "tenderSource", event.target.value)
                        }
                        placeholder="Preliminaries.pdf"
                      />
                    </label>
                    <label>
                      Clause
                      <input
                        value={finding.clauseReference}
                        onChange={(event) =>
                          updateFinding(index, "clauseReference", event.target.value)
                        }
                        placeholder="Section 8.4"
                      />
                    </label>
                    <label>
                      Confidence
                      <input
                        value={finding.confidence}
                        onChange={(event) =>
                          updateFinding(index, "confidence", event.target.value)
                        }
                        placeholder="High"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Contractor evidence
                      <textarea
                        rows={2}
                        value={finding.contractorEvidence}
                        onChange={(event) =>
                          updateFinding(index, "contractorEvidence", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Evidence source
                      <input
                        value={finding.contractorEvidenceSource}
                        onChange={(event) =>
                          updateFinding(index, "contractorEvidenceSource", event.target.value)
                        }
                        placeholder="Insurance schedule"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Why it matters
                      <textarea
                        rows={2}
                        value={finding.whyItMatters}
                        onChange={(event) =>
                          updateFinding(index, "whyItMatters", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Required action
                      <textarea
                        rows={2}
                        value={finding.requiredAction}
                        onChange={(event) =>
                          updateFinding(index, "requiredAction", event.target.value)
                        }
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="gatecheck-template-page__stack">
              <div className="gatecheck-template-page__subsection-head">
                <h3>Next actions</h3>
                <button
                  type="button"
                  className="gatecheck-template-page__ghost-button"
                  onClick={addAction}
                >
                  Add action
                </button>
              </div>
              {decisionDraft.nextActions.map((action, index) => (
                <div key={`action-${index}`} className="gatecheck-template-page__nested-card">
                  <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--three">
                    <label>
                      Owner
                      <input
                        value={action.owner}
                        onChange={(event) =>
                          updateAction(index, "owner", event.target.value)
                        }
                        placeholder="Compliance"
                      />
                    </label>
                    <label>
                      Due label
                      <input
                        value={action.dueLabel}
                        onChange={(event) =>
                          updateAction(index, "dueLabel", event.target.value)
                        }
                        placeholder="Before estimate kickoff"
                      />
                    </label>
                    <label>
                      Status
                      <select
                        value={action.status || "Open"}
                        onChange={(event) =>
                          updateAction(index, "status", event.target.value)
                        }
                      >
                        {["Open", "Completed", "Waived"].map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="gatecheck-template-page__inline-actions">
                      <button
                        type="button"
                        className="gatecheck-template-page__ghost-button gatecheck-template-page__ghost-button--danger"
                        onClick={() => removeAction(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="gatecheck-template-page__field-span-3">
                      Action
                      <textarea
                        rows={2}
                        value={action.action}
                        onChange={(event) =>
                          updateAction(index, "action", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Linked finding
                      <input
                        value={action.linkedFinding || ""}
                        onChange={(event) =>
                          updateAction(index, "linkedFinding", event.target.value)
                        }
                        placeholder="Optional. Link this action to a finding title or id."
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="gatecheck-template-page__stack">
              <div className="gatecheck-template-page__subsection-head">
                <h3>Decision history</h3>
                <button
                  type="button"
                  className="gatecheck-template-page__ghost-button"
                  onClick={addHistoryEntry}
                >
                  Add history entry
                </button>
              </div>
              {decisionDraft.decisionHistory.map((entry, index) => (
                <div key={`history-${index}`} className="gatecheck-template-page__nested-card">
                  <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--three">
                    <label>
                      Date
                      <input
                        type="datetime-local"
                        value={entry.date ? entry.date.slice(0, 16) : ""}
                        onChange={(event) =>
                          updateHistoryEntry(
                            index,
                            "date",
                            event.target.value
                              ? new Date(event.target.value).toISOString()
                              : ""
                          )
                        }
                      />
                    </label>
                    <label>
                      State
                      <select
                        value={entry.state}
                        onChange={(event) =>
                          updateHistoryEntry(index, "state", event.target.value)
                        }
                      >
                        {GATECHECK_ALLOWED_DECISIONS.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Owner
                      <input
                        value={entry.owner}
                        onChange={(event) =>
                          updateHistoryEntry(index, "owner", event.target.value)
                        }
                        placeholder="Commercial"
                      />
                    </label>
                    <label>
                      Origin
                      <input
                        value={entry.origin || ""}
                        onChange={(event) =>
                          updateHistoryEntry(index, "origin", event.target.value)
                        }
                        placeholder="Layerbound assessment"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Note
                      <textarea
                        rows={2}
                        value={entry.note}
                        onChange={(event) =>
                          updateHistoryEntry(index, "note", event.target.value)
                        }
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    className="gatecheck-template-page__ghost-button gatecheck-template-page__ghost-button--danger"
                    onClick={() => removeHistoryEntry(index)}
                  >
                    Remove history entry
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="gatecheck-template-page__side">
          <ChecklistCard
            title="What the live link proves"
            items={[
              "Decision",
              "Rationale",
              "Blockers and evidence gaps",
              "Owner and next action",
              "Live decision history",
            ]}
          />

          <section className="gatecheck-template-page__card">
            <h2>Publish check</h2>
            {validationErrors.length ? (
              <div className="gatecheck-template-page__validation">
                {validationErrors.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>
            ) : (
              <p className="gatecheck-template-page__success">
                Core Gatecheck requirements are filled. This workspace is ready to publish.
              </p>
            )}
            <form className="gatecheck-template-page__publish-form" onSubmit={publishFlow}>
              <button type="submit" disabled={publishing}>
                {publishing ? "Publishing..." : "Publish Gatecheck link"}
              </button>
            </form>
            {error ? <p className="gatecheck-template-page__error">{error}</p> : null}
            {publishResult?.publicUrl ? (
              <div className="gatecheck-template-page__result">
                <span>Latest link</span>
                <a href={publishResult.publicUrl} target="_blank" rel="noreferrer">
                  {publishResult.publicUrl}
                </a>
              </div>
            ) : null}
          </section>

          <section className="gatecheck-template-page__card">
            <h2>Derived live record</h2>
            <p className="gatecheck-template-page__muted">
              This is the publish payload assembled from the structured workspace.
            </p>
            <textarea
              className="gatecheck-template-page__json-preview"
              value={prettyJson(gatecheckRecord)}
              readOnly
              rows={24}
              spellCheck="false"
            />
          </section>

          <section className="gatecheck-template-page__card">
            <h2>Live links</h2>
            <p className="gatecheck-template-page__muted">
              Published Gatecheck links for this business.
            </p>
            {loading ? (
              <p className="gatecheck-template-page__muted">Loading links...</p>
            ) : null}
            {!loading && !publishes.length ? (
              <p className="gatecheck-template-page__muted">No live Gatecheck links yet.</p>
            ) : null}
            <div className="gatecheck-template-page__links">
              {publishes.map((entry) => (
                <article
                  key={entry.publishedAt}
                  className="gatecheck-template-page__link-card"
                >
                  <div>
                    <strong>{entry.storeSlug || entry.storeId}</strong>
                    <p>
                      Published {new Date(entry.publishedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <a href={entry.htmlUrl || entry.publicUrl} target="_blank" rel="noreferrer">
                    Open link
                  </a>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
